import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProjectContext = createContext(null);

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error("useProject must be used within ProjectProvider");
  }
  return context;
};

export const ProjectProvider = ({ children }) => {
  const { token, isAdmin } = useAuth();
  // Admin-side state
  const [projects, setProjects] = useState([]);
  const [currentProject, setCurrentProject] = useState(null);
  const [loading, setLoading] = useState(true);
  // Public-site state (resolved from window.location.host)
  const [publicProject, setPublicProject] = useState(null);
  const [publicProjectLoading, setPublicProjectLoading] = useState(true);

  useEffect(() => {
    const fetchProjects = async () => {
      if (token && isAdmin) {
        try {
          const response = await axios.get(`${API}/projects`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setProjects(response.data);
          if (response.data.length > 0 && !currentProject) {
            const stored = localStorage.getItem("currentProjectId");
            const found = response.data.find(p => p.id === stored);
            setCurrentProject(found || response.data[0]);
          }
        } catch (error) {
          console.error("Failed to fetch projects:", error);
        }
      }
      setLoading(false);
    };
    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isAdmin]);

  // Resolve public project from request hostname (multi-domain routing).
  // Override: if URL has ?preview=<slug>, resolve by slug instead (used by admin "Preview" button).
  useEffect(() => {
    const resolvePublicProject = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const previewSlug = params.get("preview");
        let response;
        if (previewSlug) {
          response = await axios.get(
            `${API}/public/projects/by-slug/${encodeURIComponent(previewSlug)}`
          );
        } else {
          const host = window.location.host;
          response = await axios.get(
            `${API}/public/projects/by-domain/${encodeURIComponent(host)}`
          );
        }
        setPublicProject(response.data);
      } catch (error) {
        console.error("Failed to resolve public project:", error);
        setPublicProject(null);
      } finally {
        setPublicProjectLoading(false);
      }
    };
    resolvePublicProject();
  }, []);

  const selectProject = (project) => {
    setCurrentProject(project);
    localStorage.setItem("currentProjectId", project.id);
  };

  const refreshProjects = async () => {
    if (token && isAdmin) {
      const response = await axios.get(`${API}/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(response.data);
      return response.data;
    }
    return [];
  };

  return (
    <ProjectContext.Provider value={{
      projects,
      currentProject,
      loading,
      selectProject,
      refreshProjects,
      publicProject,
      publicProjectLoading
    }}>
      {children}
    </ProjectContext.Provider>
  );
};
